import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { ArrowLeft, ArrowRight, Gamepad2, Loader2 } from 'lucide-react'
import { handleValidationErrors } from '@/shared/utils'
import { validateStep4, validateHoursPerDay, validateMonthlySpending, getSubscriptionsForGames } from '../validation/gamingDetailsValidation'
import SchoolInfo from './SchoolInfo'
import MonthlySubscriptions from './MonthlySubscriptions'
import EducationLevel from './EducationLevel'
import GamingHistory from './GamingHistory'
import GamingSpending from './GamingSpending'
import ProofOfGaming from './ProofOfGaming'
import GamingIncentives from './GamingIncentives'
import type { Step4GamingDetailsProps, ValidationErrors } from '@/features/enrollment'

export default function Step4GamingDetails({ enrollmentData, updateEnrollmentData, updateStepData, handleNavigate, handleSimpleNavigate, isNavigating, isCompleting }: Step4GamingDetailsProps) {
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [subscriptionsAutoSelected, setSubscriptionsAutoSelected] = useState(false)
  const [educationLevelAutoSelected, setEducationLevelAutoSelected] = useState(false)
  const [proofOfGamingAutoSelected, setProofOfGamingAutoSelected] = useState(false)
  const [monthlySpendingAutoSelected, setMonthlySpendingAutoSelected] = useState(false)
  const [hoursPerDayAutoSelected, setHoursPerDayAutoSelected] = useState(false)
  const [originalMonthlySpending, setOriginalMonthlySpending] = useState<string | null>(null)
  const [originalEducationLevel, setOriginalEducationLevel] = useState<string | null>(null)

  // Get step4 data or fallback to top-level data (memoized to prevent infinite loops)
  const stepData = useMemo(() => enrollmentData, [enrollmentData])

  // Helper function to update step data (memoized to prevent infinite loops)
  const updateField = useCallback((field: string, value: any) => {
    // if (updateStepData) {
    //   updateStepData(3, field, value) // Step 4 (0-based index 3)
    // } else {
    //   updateEnrollmentData(field, value)
    // }
    updateEnrollmentData(field, value)

  }, [updateStepData, updateEnrollmentData])

  const handleValidateHoursPerDay = (value: string) => {
    validateHoursPerDay(value, errors, setErrors)
  }

  const handleValidateMonthlySpending = (value: string) => {
    validateMonthlySpending(value, errors, setErrors)
  }

  // Auto-populate fields from previous steps
  useEffect(() => {
    // Auto-populate gaming platforms from Step1/Step2
    if (!stepData.gamingPlatforms && enrollmentData.intakePlatforms) {
      const mappedPlatforms = enrollmentData.intakePlatforms.map((platform: string) => {
        switch (platform) {
          case 'PlayStation': return 'Playstation'
          case 'Xbox': return 'Xbox'
          case 'Steam': return 'Steam'
          default: return platform
        }
      })
      updateField('gamingPlatforms', mappedPlatforms)
    }

    // Auto-populate video games from Step2
    if (!stepData.videoGamesPlayed && enrollmentData.intakeGames) {
      updateField('videoGamesPlayed', enrollmentData.intakeGames)
    }

    // Set default values for required fields
    if (stepData.isCurrentlyInSchool === undefined || stepData.isCurrentlyInSchool === null) {
      updateField('isCurrentlyInSchool', false) // Default to not in school
    }
    if (!stepData.firstVideoGame) {
      // Randomly select from the specified games
      const firstGames = ['GTA', 'Grand Theft Auto', 'Roblox', 'Minecraft', 'Fortnite']
      const randomIndex = Math.floor(Math.random() * firstGames.length)
      const selectedGame = firstGames[randomIndex]
      updateField('firstVideoGame', selectedGame)
    }
    if (stepData.paidForInGameItems === undefined || stepData.paidForInGameItems === null) {
      updateField('paidForInGameItems', 'Yes')
    }
    if (stepData.soldInGameContent === undefined || stepData.soldInGameContent === null) {
      updateField('soldInGameContent', 'No')
    }
    if (stepData.receivedEnergyDrinkReward === undefined || stepData.receivedEnergyDrinkReward === null) {
      updateField('receivedEnergyDrinkReward', 'No')
    }

  }, []) // Empty dependency array to prevent infinite loop

  // Auto-populate average hours from Step2 (only once)
  useEffect(() => {
    if (enrollmentData.hoursPerDay && (!stepData.averageHoursPerDay || stepData.averageHoursPerDay !== enrollmentData.hoursPerDay) && !hoursPerDayAutoSelected) {
      updateField('averageHoursPerDay', enrollmentData.hoursPerDay)
      setHoursPerDayAutoSelected(true) // Mark as auto-selected to prevent re-running
    } else if (hoursPerDayAutoSelected) {
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-populate monthly subscriptions based on games from Step2 (only once)
  useEffect(() => {
    if (enrollmentData.intakeGames && enrollmentData.intakeGames.length > 0 && !subscriptionsAutoSelected) {
      const suggestedSubscriptions = getSubscriptionsForGames(enrollmentData.intakeGames)
      const currentSubscriptions = stepData.monthlySubscriptions || []
      
      // Only add subscriptions that aren't already selected
      const newSubscriptions = suggestedSubscriptions.filter((sub: string) => !currentSubscriptions.includes(sub))
      
      if (newSubscriptions.length > 0) {
        // Remove "No Monthly Subscription" if it exists when adding new subscriptions
        const filteredCurrent = currentSubscriptions.filter((sub: string) => sub !== 'No Monthly Subscription')
        const updatedSubscriptions = [...filteredCurrent, ...newSubscriptions]
        
        updateField('monthlySubscriptions', updatedSubscriptions)
        setSubscriptionsAutoSelected(true) // Mark as auto-selected to prevent re-running
      }
    } else if (subscriptionsAutoSelected) {
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-populate injured party DOB from birth information
  useEffect(() => {
    if (!stepData.injuredPartyDOB && enrollmentData.birthMonth && enrollmentData.birthDay && enrollmentData.birthYear) {
      const month = enrollmentData.birthMonth.toString().padStart(2, '0')
      const day = enrollmentData.birthDay.toString().padStart(2, '0')
      const year = enrollmentData.birthYear
      const dob = `${year}-${month}-${day}`
      updateField('injuredPartyDOB', dob)
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-select education level based on age (only once)
  useEffect(() => {
    
    if (stepData.injuredPartyDOB && !educationLevelAutoSelected) {
      const birthDate = new Date(stepData.injuredPartyDOB)
      const today = new Date()
      const age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      
      // Adjust age if birthday hasn't occurred this year
      const adjustedAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
        ? age - 1 
        : age
      
      
      let educationLevel = ''
      
      if (adjustedAge >= 1 && adjustedAge <= 12) {
        educationLevel = 'Elementary/Junior High School'
      } else if (adjustedAge >= 13 && adjustedAge <= 18) {
        educationLevel = 'High School'
      } else if (adjustedAge >= 19 && adjustedAge <= 27) {
        // 50/50 chance between High School and College/University
        const random = Math.random()
        educationLevel = random < 0.5 ? 'High School' : 'College/University'
      } else {
        // Default fallback for other ages
        educationLevel = 'High School'
      }
      
      // Auto-select the education level and mark as auto-selected
      if (educationLevel) {
        updateField('highestEducationLevel', educationLevel)
        setOriginalEducationLevel(educationLevel) // Store the original auto-selected value
        setEducationLevelAutoSelected(true) // Mark as auto-selected to prevent re-running
      }
    } else {
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-select "Yes" for cloud gaming subscription - separate useEffect for reliability
  useEffect(() => {
    if (!stepData.cloudGamingSubscription || stepData.cloudGamingSubscription === '') {
      updateField('cloudGamingSubscription', 'Yes')
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-select VR accessories with 90% probability for "Yes"
  useEffect(() => {
    if (!stepData.vrAccessories || stepData.vrAccessories === '') {
      const random = Math.random()
      const selection = random < 0.9 ? 'Yes' : 'No' // 90% chance for "Yes"
      updateField('vrAccessories', selection)
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-select receipts with 80% probability for "Yes"
  useEffect(() => {
    if (!stepData.hasReceipts || stepData.hasReceipts === '') {
      const random = Math.random()
      const selection = random < 0.8 ? 'Yes' : 'No' // 80% chance for "Yes"
      updateField('hasReceipts', selection)
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-populate monthly spending with random amount between $50-$200 (only once)
  useEffect(() => {
    if ((!stepData.monthlySpending || stepData.monthlySpending === '') && !monthlySpendingAutoSelected) {
      const randomAmount = Math.floor(Math.random() * (200 - 50 + 1)) + 50 // Random between 50-200
      updateField('monthlySpending', randomAmount.toString())
      setOriginalMonthlySpending(randomAmount.toString()) // Store the original auto-populated value
      setMonthlySpendingAutoSelected(true) // Mark as auto-selected to prevent re-running
    } else if (monthlySpendingAutoSelected) {
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-select tried to stop gaming with 95% probability for "Yes"
  useEffect(() => {
    if (!stepData.triedToStopGaming || stepData.triedToStopGaming === '') {
      const random = Math.random()
      const selection = random < 0.95 ? 'Yes' : 'No' // 95% chance for "Yes"
      updateField('triedToStopGaming', selection)
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-select "Yes" for watched gaming influencers
  useEffect(() => {
    if (!stepData.watchedGamingInfluencers || stepData.watchedGamingInfluencers === '') {
      updateField('watchedGamingInfluencers', 'Yes')
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-select "Photos" for proof of gaming (only once)
  useEffect(() => {
    if ((!stepData.proofOfGaming || stepData.proofOfGaming.length === 0) && !proofOfGamingAutoSelected) {
      updateField('proofOfGaming', ['Photos'])
      setProofOfGamingAutoSelected(true) // Mark as auto-selected to prevent re-running
    } else if (proofOfGamingAutoSelected) {
    }
  }, []) // Empty dependency array to prevent infinite loop

  // Auto-calculate first gaming date based on birth year + age started gaming
  useEffect(() => {
    if (enrollmentData.birthYear && enrollmentData.ageStartedGaming) {
      const birthYear = parseInt(enrollmentData.birthYear)
      const ageStarted = parseInt(enrollmentData.ageStartedGaming)
      
      if (!isNaN(birthYear) && !isNaN(ageStarted)) {
        const firstGamingYear = birthYear + ageStarted
        const currentYear = new Date().getFullYear()
        
        // Validation: Check for unrealistic scenarios
        if (ageStarted < 1) {
          console.warn(`Invalid age started gaming: ${ageStarted}. Must be at least 1 year old.`)
          return
        }
        
        // Calculate current age of the gamer for additional validation
        if (enrollmentData.birthMonth && enrollmentData.birthDay) {
          const birthMonth = parseInt(enrollmentData.birthMonth)
          const birthDay = parseInt(enrollmentData.birthDay)
          
          const today = new Date()
          const birthDate = new Date(birthYear, birthMonth - 1, birthDay)
          const currentAge = Math.floor((today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
          
          if (ageStarted > currentAge) {
            console.warn(`Invalid age started gaming: ${ageStarted}. Cannot be older than current age (${currentAge}).`)
            return
          }
        }
        
        if (ageStarted > 80) {
          console.warn(`Unrealistic age started gaming: ${ageStarted}. Most people don't start gaming after 80.`)
          return
        }
        
        if (firstGamingYear > currentYear) {
          console.warn(`Invalid calculation: First gaming year ${firstGamingYear} is in the future. Birth year: ${birthYear}, Age started: ${ageStarted}`)
          return
        }
        
        // Gaming didn't exist before 1970s, so this is another validation
        if (firstGamingYear < 1975) {
          console.warn(`Unrealistic first gaming year: ${firstGamingYear}. Video games weren't widely available before 1975.`)
          return
        }
        
        // Check if we need to update the date (either empty or calculated year is different)
        const currentDate = stepData.firstGamingDate
        const currentDateYear = currentDate ? parseInt(currentDate.split('-')[0]) : null
        
        if (!currentDate || currentDateYear !== firstGamingYear) {
          // Generate random month (1-12) and day (1-28 to avoid invalid dates)
          const randomMonth = Math.floor(Math.random() * 12) + 1
          const randomDay = Math.floor(Math.random() * 28) + 1
          
          // Format as YYYY-MM-DD for date input
          const formattedMonth = randomMonth.toString().padStart(2, '0')
          const formattedDay = randomDay.toString().padStart(2, '0')
          const calculatedDate = `${firstGamingYear}-${formattedMonth}-${formattedDay}`
          
          updateField('firstGamingDate', calculatedDate)
        }
      }
    }
  }, []) // Empty dependency array to prevent infinite loop

  const handleContinue = () => {
    const validationErrors = validateStep4(stepData)
    setErrors(validationErrors)
    
    // Debug: Log validation errors
    if (Object.keys(validationErrors).length > 0) {
      console.error('Step 4 validation errors:', validationErrors)
    }
    
    if (Object.keys(validationErrors).length === 0) {
      handleNavigate(4)
    } else {
      // Scroll to first error field and highlight all errors
      handleValidationErrors(validationErrors)
    }
  }

  const handleBack = () => {
    setErrors({})
    if (handleSimpleNavigate) {
      handleSimpleNavigate(2) // Go to Step3 (index 2)
    } else {
      handleNavigate(2)
    }
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
            <Gamepad2 className="w-6 h-6 text-secondary-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-accent-900">Gaming Details</h3>
            <p className="text-sm text-accent-600">Comprehensive gaming information and history.</p>
          </div>
        </div>

        <div className="space-y-6">
          <SchoolInfo
            enrollmentData={stepData}
            updateEnrollmentData={updateField}
            errors={errors}
            isCurrentlyInSchool={stepData.isCurrentlyInSchool ?? false}
          />


          <MonthlySubscriptions
            enrollmentData={stepData}
            updateEnrollmentData={updateField}
            errors={errors}
            intakeGames={enrollmentData.intakeGames}
            subscriptionsAutoSelected={subscriptionsAutoSelected}
            setSubscriptionsAutoSelected={setSubscriptionsAutoSelected}
            getSubscriptionsForGames={getSubscriptionsForGames}
          />

          <EducationLevel
            enrollmentData={stepData}
            updateEnrollmentData={updateField}
            errors={errors}
            injuredPartyDOB={enrollmentData.injuredPartyDOB}
            originalEducationLevel={originalEducationLevel}
            educationLevelAutoSelected={educationLevelAutoSelected}
            setEducationLevelAutoSelected={setEducationLevelAutoSelected}
          />

          <GamingHistory
            enrollmentData={stepData}
            updateEnrollmentData={updateField}
            errors={errors}
            hoursPerDayAutoSelected={hoursPerDayAutoSelected}
            setHoursPerDayAutoSelected={setHoursPerDayAutoSelected}
            validateHoursPerDay={handleValidateHoursPerDay}
          />

          <GamingSpending
            enrollmentData={stepData}
            updateEnrollmentData={updateField}
            errors={errors}
            originalMonthlySpending={originalMonthlySpending}
            monthlySpendingAutoSelected={monthlySpendingAutoSelected}
            setMonthlySpendingAutoSelected={setMonthlySpendingAutoSelected}
            validateMonthlySpending={handleValidateMonthlySpending}
          />

          <ProofOfGaming
            enrollmentData={stepData}
            updateEnrollmentData={updateField}
            errors={errors}
            proofOfGamingAutoSelected={proofOfGamingAutoSelected}
            setProofOfGamingAutoSelected={setProofOfGamingAutoSelected}
          />

          <GamingIncentives
            enrollmentData={stepData}
            updateEnrollmentData={updateField}
            errors={errors}
          />
        </div>
      </div>

      <div className="flex justify-between">
        <button
          className="btn-outline flex items-center space-x-2"
          onClick={handleBack}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
    <button
      className="btn-primary flex items-center space-x-2"
      onClick={handleContinue}
      disabled={isNavigating}
    >
      {isNavigating ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <ArrowRight className="w-4 h-4" />
      )}
      <span>{isNavigating ? 'Saving...' : 'Continue'}</span>
    </button>
      </div>
    </div>
  )
}
